import { useTrackImpressionEventByIntersection } from "@product/scmp-sdk";
import { useCallback } from "react";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { Event } from "~/components/tracking/google-analytics/types";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "~/components/tracking/google-analytics-4/types";

export const commentHash = "#comments";

export const useTopStoriesImpressionTracking = (listOfArticles: string[]) => {
  const firstSetImpressionEvent = useCallback<() => Event>(
    () => ({
      action: "Impression/top_story_6",
      category: "Homepage Depth",
    }),
    [],
  );
  const firstImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles.slice(0, 4).join(","),
        page_type: "homepage",
        widget_name: "top_story5",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );
  const {
    captureTrackImpressionEventTargetElement: captureTrackImpressionEventTargetFirstSetElement,
  } = useTrackImpressionEventByIntersection<Event, RecirculationWidgetImpressionEvent>({
    ga4TrackingHandler: sendGA4Tracking,
    gaTrackingHandler: sendGATracking,
    getEvent: firstSetImpressionEvent,
    getGa4Event: firstImpressionGA4Event,
    options: { isSendGA4Tracking: true, isSendGATracking: true, shouldSendOnce: true },
  });

  const secondSetImpressionEvent = useCallback<() => Event>(
    () => ({
      action: "Impression/top_story_9",
      category: "Homepage Depth",
    }),
    [],
  );
  const secondImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles.slice(4, 8).join(","),
        page_type: "homepage",
        widget_name: "top_story8",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );
  const {
    captureTrackImpressionEventTargetElement: captureTrackImpressionEventTargetSecondSetElement,
  } = useTrackImpressionEventByIntersection<Event, RecirculationWidgetImpressionEvent>({
    ga4TrackingHandler: sendGA4Tracking,
    gaTrackingHandler: sendGATracking,
    getEvent: secondSetImpressionEvent,
    getGa4Event: secondImpressionGA4Event,
    options: { isSendGA4Tracking: true, isSendGATracking: true, shouldSendOnce: true },
  });

  const thirdSetImpressionEvent = useCallback<() => Event>(
    () => ({
      action: "Impression/top_story_13",
      category: "Homepage Depth",
    }),
    [],
  );
  const thirdImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles.slice(8, 12).join(","),
        page_type: "homepage",
        widget_name: "top_story12",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );
  const {
    captureTrackImpressionEventTargetElement: captureTrackImpressionEventTargetThirdSetElement,
  } = useTrackImpressionEventByIntersection<Event, RecirculationWidgetImpressionEvent>({
    ga4TrackingHandler: sendGA4Tracking,
    gaTrackingHandler: sendGATracking,
    getEvent: thirdSetImpressionEvent,
    getGa4Event: thirdImpressionGA4Event,
    options: { isSendGA4Tracking: true, isSendGATracking: true, shouldSendOnce: true },
  });

  const fourthSetImpressionEvent = useCallback<() => Event>(
    () => ({
      action: "Impression/top_story_17",
      category: "Homepage Depth",
    }),
    [],
  );
  const fourthImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles.slice(12).join(","),
        page_type: "homepage",
        widget_name: "top_story16",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );
  const {
    captureTrackImpressionEventTargetElement: captureTrackImpressionEventTargetFourthSetElement,
  } = useTrackImpressionEventByIntersection<Event, RecirculationWidgetImpressionEvent>({
    ga4TrackingHandler: sendGA4Tracking,
    gaTrackingHandler: sendGATracking,
    getEvent: fourthSetImpressionEvent,
    getGa4Event: fourthImpressionGA4Event,
    options: { isSendGA4Tracking: true, isSendGATracking: true, shouldSendOnce: true },
  });

  const firstFoldImpressionEvent = useCallback<() => Event>(
    () => ({
      action: "Impression/first_fold",
      category: "Homepage Depth",
    }),
    [],
  );
  const {
    captureTrackImpressionEventTargetElement: captureTrackImpressionEventTargetFirstFoldElement,
  } = useTrackImpressionEventByIntersection<Event, null>({
    gaTrackingHandler: sendGATracking,
    getEvent: firstFoldImpressionEvent,
    options: { isSendGATracking: true, shouldSendOnce: true },
  });

  const secondFoldImpressionEvent = useCallback<() => Event>(
    () => ({
      action: "Impression/second_fold",
      category: "Homepage Depth",
    }),
    [],
  );
  const {
    captureTrackImpressionEventTargetElement: captureTrackImpressionEventTargetSecondFoldElement,
  } = useTrackImpressionEventByIntersection<Event, null>({
    gaTrackingHandler: sendGATracking,
    getEvent: secondFoldImpressionEvent,
    options: { isSendGATracking: true, shouldSendOnce: true },
  });

  const thirdFoldImpressionEvent = useCallback<() => Event>(
    () => ({
      action: "Impression/third_fold",
      category: "Homepage Depth",
    }),
    [],
  );
  const {
    captureTrackImpressionEventTargetElement: captureTrackImpressionEventTargetThirdFoldElement,
  } = useTrackImpressionEventByIntersection<Event, null>({
    gaTrackingHandler: sendGATracking,
    getEvent: thirdFoldImpressionEvent,
    options: { isSendGATracking: true, shouldSendOnce: true },
  });

  return {
    captureTrackImpressionEventTargetFirstFoldElement,
    captureTrackImpressionEventTargetFirstSetElement,
    captureTrackImpressionEventTargetFourthSetElement,
    captureTrackImpressionEventTargetSecondFoldElement,
    captureTrackImpressionEventTargetSecondSetElement,
    captureTrackImpressionEventTargetThirdFoldElement,
    captureTrackImpressionEventTargetThirdSetElement,
  };
};
